import { useCallback, useMemo, useState } from 'react'
import type { Entry } from '@/types/domain'
import { computeTotals } from '@/core/aggregation/totals'
import { useEntriesStore } from '@/core/storage/zustand-store'
import { useEntryMutations, type EntryFormValues } from '@/hooks/useEntryMutations'
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts'
import { useTTLGuard } from '@/hooks/useTTLGuard'
import { ApiHealthBanner } from '../components/ApiHealthBanner'
import { EntryDrawer } from '../components/EntryDrawer'
import { ExportDialog } from '../components/ExportDialog'
import { Header } from '../components/Header'
import { ImportDialog } from '../components/ImportDialog'
import { MonthlyMatrixView } from '../components/MonthlyMatrix'
import { PrivacyBanner } from '../components/PrivacyBanner'
import { Section, type SortColumn, type SortState } from '../components/Section'
import { SettingsDrawer } from '../components/SettingsDrawer'
import { ShortcutsHelpModal } from '../components/ShortcutsHelpModal'
import { TotalsFooter } from '../components/TotalsFooter'
import { YearBar, type ViewMode } from '../components/YearBar'
import { EmptyState } from './EmptyState'

type DrawerState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; entryId: string }
  | { mode: 'copy'; entryId: string }

export function MainScreen() {
  const allEntries = useEntriesStore((s) => s.entries)
  const currentYear = new Date().getFullYear()
  const mutations = useEntryMutations()

  const years = useMemo(() => {
    const set = new Set<number>(allEntries.map((e) => e.anoBase))
    set.add(currentYear)
    return Array.from(set).sort((a, b) => b - a)
  }, [allEntries, currentYear])

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [drawer, setDrawer] = useState<DrawerState>({ mode: 'closed' })
  const [exportOpen, setExportOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('split')
  const [sort, setSort] = useState<SortState>({ column: 'data', direction: 'desc' })

  useTTLGuard()

  const yearEntries = useMemo(
    () => allEntries.filter((e) => e.anoBase === selectedYear),
    [allEntries, selectedYear],
  )

  // Stable numbering per year-base: order by criadoEm ASC so N° 001 is
  // always the first lançamento created in this ano-base, regardless of
  // the currently active table sort.
  const numeroMap = useMemo(() => {
    const sortedByCreation = [...yearEntries].sort((a, b) =>
      a.criadoEm.localeCompare(b.criadoEm),
    )
    const map = new Map<string, number>()
    sortedByCreation.forEach((entry, idx) => map.set(entry.id, idx + 1))
    return map
  }, [yearEntries])

  const numeroOf = useCallback((id: string) => numeroMap.get(id) ?? 0, [numeroMap])

  const sortEntries = useCallback(
    (list: Entry[]): Entry[] => {
      const next = [...list]
      next.sort((a, b) => {
        let cmp = 0
        if (sort.column === 'data') {
          cmp = a.dataRecebimento.localeCompare(b.dataRecebimento)
        } else {
          cmp = (numeroMap.get(a.id) ?? 0) - (numeroMap.get(b.id) ?? 0)
        }
        return sort.direction === 'asc' ? cmp : -cmp
      })
      return next
    },
    [sort, numeroMap],
  )

  const rendimentos = useMemo(
    () => sortEntries(yearEntries.filter((e) => e.tipo === 'rendimento')),
    [yearEntries, sortEntries],
  )
  const impostos = useMemo(
    () => sortEntries(yearEntries.filter((e) => e.tipo === 'imposto_pago_exterior')),
    [yearEntries, sortEntries],
  )
  const chronological = useMemo(() => sortEntries(yearEntries), [yearEntries, sortEntries])

  const onSort = useCallback((column: SortColumn): void => {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: column === 'numero' ? 'asc' : 'desc' },
    )
  }, [])

  const totals = useMemo(() => computeTotals(yearEntries), [yearEntries])
  const lastUpdatedAt = useMemo(() => mostRecentCalculo(yearEntries), [yearEntries])
  const isEmpty = yearEntries.length === 0

  const editingEntry =
    drawer.mode === 'edit' ? allEntries.find((e) => e.id === drawer.entryId) ?? null : null
  const templateEntry =
    drawer.mode === 'copy' ? allEntries.find((e) => e.id === drawer.entryId) ?? null : null

  const onNew = useCallback(() => setDrawer({ mode: 'create' }), [])
  const onEdit = useCallback((entry: Entry) => setDrawer({ mode: 'edit', entryId: entry.id }), [])
  const onCopy = useCallback((entry: Entry) => setDrawer({ mode: 'copy', entryId: entry.id }), [])
  const onCloseDrawer = useCallback(() => setDrawer({ mode: 'closed' }), [])
  const onRecalc = useCallback(
    (entry: Entry) => {
      void mutations.recalculateEntry(entry.id)
    },
    [mutations],
  )
  const onDelete = useCallback(
    (entry: Entry) => {
      const ok = window.confirm(
        `Excluir lançamento de ${entry.dataRecebimento} (${entry.moedaOrigem} ${entry.valorOriginal})?`,
      )
      if (ok) mutations.deleteEntry(entry.id)
    },
    [mutations],
  )

  const onSubmit = useCallback(
    (values: EntryFormValues) => {
      if (drawer.mode === 'edit') {
        mutations.updateEntry(drawer.entryId, values)
      } else {
        mutations.createEntry(values)
      }
    },
    [drawer, mutations],
  )

  const onRecalcAll = useCallback(() => {
    void mutations.recalculateAll()
  }, [mutations])

  useKeyboardShortcuts({
    onNew,
    onRecalcAll,
    onExport: () => setExportOpen(true),
    onImport: () => setImportOpen(true),
    onSettings: () => setSettingsOpen(true),
    onHelp: () => setHelpOpen(true),
  })

  return (
    <div className="min-h-screen bg-muted/20">
      <PrivacyBanner />
      <Header
        onImport={() => setImportOpen(true)}
        onExport={() => setExportOpen(true)}
        onSettings={() => setSettingsOpen(true)}
      />
      <ApiHealthBanner />
      <YearBar
        years={years}
        selectedYear={selectedYear}
        onSelectYear={setSelectedYear}
        totalEntries={yearEntries.length}
        rendimentosCount={rendimentos.length}
        impostosCount={impostos.length}
        lastUpdatedAt={lastUpdatedAt}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onNew={onNew}
        onRecalcAll={onRecalcAll}
      />
      {mutations.bulkProgress ? (
        <div className="container py-2 text-[12px] text-muted-foreground">
          recalculando&nbsp;
          <span className="font-mono">{mutations.bulkProgress.done}</span>&nbsp;de&nbsp;
          <span className="font-mono">{mutations.bulkProgress.total}</span>…
        </div>
      ) : null}

      <main className="container py-6 space-y-5">
        {isEmpty ? (
          <EmptyState anoBase={selectedYear} onNew={onNew} onImport={() => setImportOpen(true)} />
        ) : viewMode === 'split' ? (
          <>
            <Section
              titulo="Rendimentos"
              totalBRL={totals.rendimentos.BRL}
              entries={rendimentos}
              numeroOf={numeroOf}
              sort={sort}
              onSort={onSort}
              onEdit={onEdit}
              onCopy={onCopy}
              onRecalc={onRecalc}
              onDelete={onDelete}
            />
            <Section
              titulo="Impostos pagos no exterior"
              totalBRL={totals.impostos.BRL}
              entries={impostos}
              numeroOf={numeroOf}
              sort={sort}
              onSort={onSort}
              onEdit={onEdit}
              onCopy={onCopy}
              onRecalc={onRecalc}
              onDelete={onDelete}
            />
            <TotalsFooter totals={totals} />
          </>
        ) : viewMode === 'chronological' ? (
          <>
            <Section
              titulo="Lançamentos do período"
              totalBRL={totals.rendimentos.BRL - totals.impostos.BRL}
              entries={chronological}
              numeroOf={numeroOf}
              sort={sort}
              onSort={onSort}
              showTipo
              emptyMessage="sem lançamentos neste ano-base"
              onEdit={onEdit}
              onCopy={onCopy}
              onRecalc={onRecalc}
              onDelete={onDelete}
            />
            <TotalsFooter totals={totals} />
          </>
        ) : (
          <MonthlyMatrixView entries={yearEntries} anoBase={selectedYear} />
        )}
      </main>

      <EntryDrawer
        open={drawer.mode !== 'closed'}
        entry={editingEntry}
        template={templateEntry}
        onClose={onCloseDrawer}
        onSubmit={onSubmit}
      />

      <ExportDialog
        open={exportOpen}
        defaultAnoBase={selectedYear}
        onClose={() => setExportOpen(false)}
      />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onExportBeforeWipe={() => {
          setSettingsOpen(false)
          setExportOpen(true)
        }}
      />
      <ShortcutsHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}

function mostRecentCalculo(entries: Entry[]): string | undefined {
  let max: string | undefined
  for (const entry of entries) {
    const ts = entry.snapshot.calculadoEm
    if (!ts) continue
    if (!max || ts > max) max = ts
  }
  return max
}
